interface VideoEmbedProps {
  src: string;
  title?: string;
  aspectRatio?: "16:9" | "4:3" | "1:1";
}

function getVideoType(src: string): "youtube" | "vimeo" | "direct" {
  if (src.includes("youtube.com") || src.includes("youtu.be")) {
    return "youtube";
  }
  if (src.includes("vimeo.com")) {
    return "vimeo";
  }
  return "direct";
}

function getYouTubeId(url: string): string | null {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
}

function getVimeoId(url: string): string | null {
  const regExp = /vimeo\.com\/(?:video\/)?(\d+)/;
  const match = url.match(regExp);
  return match ? match[1] : null;
}

export function VideoEmbed({ 
  src, 
  title = "Video", 
  aspectRatio = "16:9" 
}: VideoEmbedProps) {
  const type = getVideoType(src);
  
  const aspectClasses = {
    "16:9": "aspect-video",
    "4:3": "aspect-[4/3]",
    "1:1": "aspect-square",
  };

  const renderVideo = () => {
    switch (type) {
      case "youtube": {
        const videoId = getYouTubeId(src);
        if (!videoId) return <p>Invalid YouTube URL</p>;
        return (
          <iframe
            src={`https://www.youtube.com/embed/${videoId}`}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
          />
        );
      }
      case "vimeo": {
        const videoId = getVimeoId(src);
        if (!videoId) return <p>Invalid Vimeo URL</p>;
        return (
          <iframe
            src={`https://player.vimeo.com/video/${videoId}`}
            title={title}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
          />
        );
      }
      case "direct":
        return (
          <video
            src={src}
            title={title}
            controls
            className="absolute inset-0 w-full h-full object-contain bg-black"
          />
        );
    }
  };

  return (
    <figure className="my-8">
      <div className={`relative ${aspectClasses[aspectRatio]} bg-[#170000]/5 rounded-lg overflow-hidden`}>
        {renderVideo()}
      </div>
      {title && title !== "Video" && (
        <figcaption className="mt-2 text-center text-sm text-[#170000]/60">
          {title}
        </figcaption>
      )}
    </figure>
  );
}
